import type {
  IEmpresaRepository,
  Empresa,
  EmpresaWithStats,
  CreateEmpresaInput,
  EmpresaUpdates,
  EmpresaLockHolder,
  EmpresaFilial,
  SalvarFilialInput,
} from '../ports/IEmpresaRepository';
import { motivoFilialInvalida, payloadDaFilial } from '../filiais';

/** A busca do select-search só dispara a partir deste nº de caracteres. */
export const MIN_EMPRESA_SEARCH_CHARS = 3;

/** Normalização usada para comparar nomes (sem acento, sem caixa, sem espaços extras).
 *  Espelha o f_unaccent(lower(nome)) do banco — usar na UI para detectar duplicata. */
export const normalizeEmpresaName = (value: string): string =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ');

export class EmpresaService {
  constructor(private readonly repo: IEmpresaRepository) {}

  listEmpresas = (workspaceId: string): Promise<Empresa[]> => {
    if (!workspaceId) return Promise.resolve([]);
    return this.repo.listEmpresas(workspaceId);
  };

  listEmpresasWithStats = (workspaceId: string): Promise<EmpresaWithStats[]> => {
    if (!workspaceId) return Promise.resolve([]);
    return this.repo.listEmpresasWithStats(workspaceId);
  };

  getLockHolders = (empresaIds: string[]): Promise<EmpresaLockHolder[]> =>
    this.repo.getLockHolders([...new Set(empresaIds.filter(Boolean))]);

  /** Busca tolerante; retorna [] enquanto a query tiver menos de MIN_EMPRESA_SEARCH_CHARS. */
  searchEmpresas = (workspaceId: string, query: string, limit?: number): Promise<Empresa[]> => {
    const q = String(query || '').trim();
    if (!workspaceId || q.length < MIN_EMPRESA_SEARCH_CHARS) return Promise.resolve([]);
    return this.repo.searchEmpresas(workspaceId, q, limit);
  };

  getEmpresa = (id: string): Promise<Empresa | null> => {
    if (!id) return Promise.resolve(null);
    return this.repo.getEmpresa(id);
  };

  getEmpresaByLead = (leadId: string): Promise<Empresa | null> => {
    if (!leadId) return Promise.resolve(null);
    return this.repo.getEmpresaByLead(leadId);
  };

  createEmpresa = (input: CreateEmpresaInput): Promise<Empresa> => {
    const nome = String(input.nome || '').trim();
    if (!input.workspaceId) throw new Error('Workspace obrigatorio.');
    if (!nome) throw new Error('Nome da empresa obrigatorio.');
    return this.repo.insertEmpresa({
      workspaceId: input.workspaceId,
      nome,
      cnpj: input.cnpj ? String(input.cnpj).trim() : null,
      endereco: input.endereco ? String(input.endereco).trim() : null,
    });
  };

  updateEmpresa = (id: string, updates: EmpresaUpdates): Promise<Empresa> => {
    if (!id) throw new Error('Empresa obrigatoria.');
    const payload: EmpresaUpdates = {};
    if (updates.nome !== undefined) {
      const n = String(updates.nome || '').trim();
      if (!n) throw new Error('Nome da empresa obrigatorio.');
      payload.nome = n;
    }
    if (updates.cnpj !== undefined) payload.cnpj = updates.cnpj ? String(updates.cnpj).trim() : null;
    if (updates.endereco !== undefined) payload.endereco = updates.endereco ? String(updates.endereco).trim() : null;
    if (updates.ativo !== undefined) payload.ativo = updates.ativo;
    if (updates.implantacaoLiberada !== undefined) payload.implantacaoLiberada = updates.implantacaoLiberada;
    return this.repo.updateEmpresa(id, payload);
  };

  deleteEmpresa = (id: string): Promise<void> => {
    if (!id) return Promise.resolve();
    return this.repo.deleteEmpresa(id);
  };

  // ── Filiais ─────────────────────────────────────────────────────
  // A validação roda contra a matriz e as filiais que a tela já tem; o banco
  // repete a trava e ainda confere as OUTRAS empresas do escopo.

  listFiliais = (empresaId: string): Promise<EmpresaFilial[]> =>
    empresaId ? this.repo.listFiliais(empresaId) : Promise.resolve([]);

  salvarFilial = async (params: {
    empresaId: string;
    filialId?: string;
    input: SalvarFilialInput;
    /** O do cadastro e o do contrato de formalização: ver motivoFilialInvalida. */
    cnpjsMatriz: (string | null | undefined)[];
    outrasFiliais: Pick<EmpresaFilial, 'id' | 'cnpj'>[];
  }): Promise<EmpresaFilial> => {
    if (!params.empresaId) throw new Error('Empresa obrigatoria.');
    const motivo = motivoFilialInvalida(params.input, {
      cnpjsMatriz: params.cnpjsMatriz, outrasFiliais: params.outrasFiliais, filialId: params.filialId,
    });
    if (motivo) throw new Error(motivo);
    const payload = payloadDaFilial(params.input);
    return params.filialId
      ? this.repo.atualizarFilial(params.filialId, payload)
      : this.repo.criarFilial(params.empresaId, payload);
  };

  removerFilial = (filialId: string): Promise<void> =>
    filialId ? this.repo.removerFilial(filialId) : Promise.resolve();
}
